import type {
  ManualReviewReadItem,
  MemberDebitState,
  MemberReceivableRetryRequest,
  MemberReceivableRetryResult,
  OrderDetail,
  PaymentMethod,
} from "@billiard/contracts";
import {
  isManualReviewListReadModel,
  isMemberDebitState,
  isMemberReceivableRetryResult,
  isOrderDetail,
  MANUAL_REVIEW_READ_CAPACITY,
} from "@billiard/contracts";

export interface CheckoutCommand {
  kind: "CHECKOUT_SESSION";
  sessionId: string;
  tableId: string;
  tableNumber: string;
  baselineOrder: OrderDetail;
  method: PaymentMethod;
  memberId: string | null;
  memberBalanceFen?: number;
  memberSpendPlanHash?: string;
  idempotencyKey: string;
}

export type CheckoutRecoveryState = "CLAIMED" | "PENDING_MEMBER_DEBIT" | "OUTCOME_UNKNOWN";

export interface CheckoutRecovery {
  ownerIdentityKey: string;
  command: CheckoutCommand;
  state: CheckoutRecoveryState;
}

export type CheckoutRecoveryView =
  | { status: "NONE" }
  | { status: "OWNED"; recovery: CheckoutRecovery }
  | { status: "OTHER_OPERATOR" }
  | { status: "RECOVERY_STORAGE_UNAVAILABLE"; pendingOutcomeMayExist: boolean };

export type CheckoutRecoveryResult =
  | "CHECKOUT_CONFIRMED"
  | "PENDING_MEMBER_DEBIT"
  | "MEMBER_RECEIVABLE";

export type MemberReceivableManualReviewItem = Extract<
  ManualReviewReadItem,
  { category: "MEMBER_RECEIVABLE" }
>;

export interface MemberReceivableRetryCommand {
  kind: "MEMBER_RECEIVABLE_RETRY";
  workItemId: string;
  baselineReview: MemberReceivableManualReviewItem;
  request: MemberReceivableRetryRequest;
}

export type MemberReceivableRetryRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN";

export interface MemberReceivableRetryRecovery {
  ownerIdentityKey: string;
  command: MemberReceivableRetryCommand;
  state: MemberReceivableRetryRecoveryState;
}

export type MemberReceivableRetryRecoveryView =
  | { status: "NONE"; canRetry: false }
  | { status: "OWNED"; recovery: MemberReceivableRetryRecovery; canRetry: boolean }
  | { status: "OTHER_OPERATOR"; canRetry: false }
  | { status: "COMMAND_MISMATCH"; canRetry: false }
  | {
      status: "RECOVERY_STORAGE_UNAVAILABLE";
      canRetry: false;
      pendingOutcomeMayExist: boolean;
    };

interface StorageAccess {
  browser: boolean;
  storage: Storage | null;
}

export const CHECKOUT_RECOVERY_STORAGE_KEY = "billiard-erp:checkout-recovery:v2";
export const LEGACY_CHECKOUT_RECOVERY_STORAGE_KEY = "billiard-erp:checkout-recovery:v1";
export const MEMBER_RECEIVABLE_RETRY_RECOVERY_STORAGE_KEY =
  "billiard-erp:member-receivable-retry-recovery:v1";

const definiteNoEffectCodes = new Set([
  "AUTH_REQUIRED",
  "AUTHORIZATION_DEFAULT_DENY",
  "CHECKOUT_TOTAL_CHANGED",
  "COACH_SERVICE_UNFINISHED",
  "CONDITIONAL_MODULE_DISABLED",
  "CSRF_REJECTED",
  "IDEMPOTENCY_COLLISION",
  "IDEMPOTENCY_KEY_REUSED",
  "INVALID_CHECKOUT_EXPECTED_TOTAL",
  "INVALID_MEMBER_PAYMENT_AMOUNT",
  "MEMBER_BALANCE_INSUFFICIENT",
  "MEMBER_CHECKOUT_IN_PROGRESS",
  "MEMBER_CHECKOUT_NOT_PREPARED",
  "MEMBER_NOT_ACTIVE",
  "MEMBER_NOT_FOUND",
  "MEMBER_PAYMENT_ALREADY_FULL",
  "MEMBER_PAYMENT_NOT_FULL",
  "MEMBER_REQUIRED",
  "MEMBER_SPEND_LINE_TOTAL_MISMATCH",
  "MEMBER_SPEND_PLAN_CHANGED",
  "MEMBER_SPEND_PREVIEW_REQUIRED",
  "MEMBER_SPEND_PREVIEW_CHANGED",
  "MEMBER_SPEND_TARGET_AMBIGUOUS",
  "MEMBER_WALLET_ACCOUNT_MISSING",
  "MEMBER_WALLET_DISABLED",
  "MIXED_MEMBER_PAYMENT_METHOD_UNAVAILABLE",
  "MIXED_MEMBER_PAYMENT_UNAVAILABLE",
  "MOBILE_SESSION_ACTIVATION_REQUIRED",
  "NATIVE_APP_VERSION_INVALID",
  "NATIVE_APP_VERSION_MISMATCH",
  "NATIVE_APP_VERSION_REQUIRED",
  "NATIVE_SECURE_SESSION_UNAVAILABLE",
  "NATIVE_SERVER_UPDATE_REQUIRED",
  "NATIVE_UPDATE_REQUIRED",
  "OFFLINE_AUTHORITY_UNAVAILABLE",
  "ONLINE_SERVICE_REQUIRED",
  "OFFICIAL_PAYMENT_REQUIRES_PROVIDER_CONFIRMATION",
  "ORDER_CONFLICT_ISOLATED",
  "ORIGIN_REJECTED",
  "PAYMENT_METHOD_DISABLED",
  "REQUEST_VALIDATION_FAILED",
  "SESSION_NOT_OPEN",
  "WALLET_PAYMENT_NOT_REQUIRED",
  "WALLET_PAYMENT_RULE_BATCH_EXPLICIT_FORBIDDEN",
  "WALLET_PAYMENT_RULE_PRECISION_MISMATCH",
  "WALLET_PAYMENT_RULE_RATIO_INVALID",
  "WALLET_PAYMENT_RULE_TARGET_INVALID",
  "WALLET_PAYMENT_RULE_TARGET_MISMATCH",
  "WALLET_PAYMENT_RULE_UNAVAILABLE",
]);

let pendingRecovery: CheckoutRecovery | null = null;
let dispatchingRecovery: CheckoutRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;
let pendingOutcomeMayExist = false;
let pendingMemberReceivableRetryRecovery: MemberReceivableRetryRecovery | null = null;
let dispatchingMemberReceivableRetryRecovery: MemberReceivableRetryRecovery | null = null;
let memberReceivableRetryHydrated = false;
let memberReceivableRetryRecoveryUnavailable = false;
let memberReceivableRetryPendingOutcomeMayExist = false;

export function checkoutIdentityKey(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): string {
  return JSON.stringify([operator.tenantId, operator.storeId, operator.id]);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(record: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(record).length === keys.length && keys.every((key) => Object.hasOwn(record, key))
  );
}

function isBoundedString(value: unknown, minimum: number, maximum: number): value is string {
  return typeof value === "string" && [...value].length >= minimum && [...value].length <= maximum;
}

function isIdentityKey(value: unknown): value is string {
  if (!isBoundedString(value, 1, 1_024)) return false;
  try {
    const identity = JSON.parse(value) as unknown;
    return (
      Array.isArray(identity) &&
      identity.length === 3 &&
      identity.every((part) => isBoundedString(part, 1, 256))
    );
  } catch {
    return false;
  }
}

function isCheckoutCommand(value: unknown): value is CheckoutCommand {
  const commonKeys = [
    "kind",
    "sessionId",
    "tableId",
    "tableNumber",
    "baselineOrder",
    "method",
    "memberId",
    "idempotencyKey",
  ] as const;
  const mixedPayment = isRecord(value) && Object.hasOwn(value, "memberBalanceFen");
  const hasMemberSpendPlan = isRecord(value) && Object.hasOwn(value, "memberSpendPlanHash");
  const commandKeys = [
    ...commonKeys,
    ...(mixedPayment ? ["memberBalanceFen" as const] : []),
    ...(hasMemberSpendPlan ? ["memberSpendPlanHash" as const] : []),
  ];
  if (
    !isRecord(value) ||
    !hasExactKeys(value, commandKeys) ||
    value.kind !== "CHECKOUT_SESSION" ||
    !isBoundedString(value.sessionId, 1, 256) ||
    !isBoundedString(value.tableId, 1, 256) ||
    !isBoundedString(value.tableNumber, 1, 64) ||
    value.sessionId !== value.sessionId.trim() ||
    value.tableId !== value.tableId.trim() ||
    value.tableNumber !== value.tableNumber.trim() ||
    !isOrderDetail(value.baselineOrder) ||
    value.baselineOrder.status !== "OPEN" ||
    value.baselineOrder.sessionId !== value.sessionId ||
    value.baselineOrder.tableId !== value.tableId ||
    value.baselineOrder.tableNumber !== value.tableNumber ||
    !["CASH", "STATIC_WECHAT", "STATIC_ALIPAY", "MEMBER_BALANCE"].includes(String(value.method)) ||
    !(value.memberId === null || isBoundedString(value.memberId, 1, 256)) ||
    (hasMemberSpendPlan &&
      (value.memberId === null ||
        typeof value.memberSpendPlanHash !== "string" ||
        !/^[a-f0-9]{64}$/u.test(value.memberSpendPlanHash))) ||
    !isBoundedString(value.idempotencyKey, 8, 128) ||
    value.idempotencyKey !== value.idempotencyKey.trim()
  ) {
    return false;
  }
  if (mixedPayment) {
    const outstandingFen = value.baselineOrder.totalFen - value.baselineOrder.paidFen;
    return (
      (value.method === "CASH" ||
        value.method === "STATIC_WECHAT" ||
        value.method === "STATIC_ALIPAY") &&
      value.memberId !== null &&
      Number.isSafeInteger(value.memberBalanceFen) &&
      Number(value.memberBalanceFen) > 0 &&
      Number(value.memberBalanceFen) < outstandingFen
    );
  }
  return value.method === "MEMBER_BALANCE"
    ? value.memberId !== null && value.baselineOrder.totalFen > value.baselineOrder.paidFen
    : value.memberId === null;
}

function immutableRecovery(
  ownerIdentityKey: string,
  command: CheckoutCommand,
  state: CheckoutRecoveryState,
): CheckoutRecovery {
  return Object.freeze({
    ownerIdentityKey,
    command: Object.freeze(structuredClone(command)),
    state,
  });
}

function storedRecovery(value: unknown): CheckoutRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    (value.version !== 1 && value.version !== 2 && value.version !== 3) ||
    !isRecord(value.recovery) ||
    !hasExactKeys(value.recovery, ["ownerIdentityKey", "command", "state"]) ||
    !isIdentityKey(value.recovery.ownerIdentityKey) ||
    !isCheckoutCommand(value.recovery.command) ||
    (value.recovery.state !== "CLAIMED" &&
      value.recovery.state !== "PENDING_MEMBER_DEBIT" &&
      value.recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  if (
    (value.version === 1 &&
      (Object.hasOwn(value.recovery.command, "memberBalanceFen") ||
        Object.hasOwn(value.recovery.command, "memberSpendPlanHash"))) ||
    (value.version === 2 && Object.hasOwn(value.recovery.command, "memberSpendPlanHash"))
  ) {
    return null;
  }
  return immutableRecovery(
    value.recovery.ownerIdentityKey,
    value.recovery.command,
    value.recovery.state,
  );
}

function recoveryEquals(left: CheckoutRecovery, right: CheckoutRecovery): boolean {
  return (
    left.ownerIdentityKey === right.ownerIdentityKey &&
    left.state === right.state &&
    JSON.stringify(left.command) === JSON.stringify(right.command)
  );
}

function recoveryCommandEquals(left: CheckoutRecovery, right: CheckoutRecovery): boolean {
  return (
    left.ownerIdentityKey === right.ownerIdentityKey &&
    JSON.stringify(left.command) === JSON.stringify(right.command)
  );
}

function sessionStorageAccess(): StorageAccess {
  if (typeof window === "undefined") return { browser: false, storage: null };
  try {
    return {
      browser: true,
      storage: typeof sessionStorage === "undefined" ? null : sessionStorage,
    };
  } catch {
    return { browser: true, storage: null };
  }
}

function readStoredRecovery(
  storage: Storage,
  key = CHECKOUT_RECOVERY_STORAGE_KEY,
): CheckoutRecovery | null {
  const encoded = storage.getItem(key);
  return encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
}

function saveStoredRecovery(recovery: CheckoutRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    access.storage.setItem(CHECKOUT_RECOVERY_STORAGE_KEY, JSON.stringify({ version: 3, recovery }));
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function verifyStoredRecovery(recovery: CheckoutRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    const stored = readStoredRecovery(access.storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function hydrateStoredRecovery(): void {
  if (hydrated) return;
  hydrated = true;
  const access = sessionStorageAccess();
  if (!access.browser) return;
  if (!access.storage) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = access.storage.getItem(CHECKOUT_RECOVERY_STORAGE_KEY);
    const legacyEncoded = access.storage.getItem(LEGACY_CHECKOUT_RECOVERY_STORAGE_KEY);
    if (encoded === null && legacyEncoded === null) return;
    pendingOutcomeMayExist = true;
    const current = encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
    const legacy =
      legacyEncoded === null ? null : storedRecovery(JSON.parse(legacyEncoded) as unknown);
    if ((encoded !== null && !current) || (legacyEncoded !== null && !legacy)) {
      recoveryUnavailable = true;
      return;
    }
    if (current && legacy && !recoveryCommandEquals(current, legacy)) {
      recoveryUnavailable = true;
      return;
    }
    const stored = current ?? legacy;
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    const recovered =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored.ownerIdentityKey, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if ((!current || stored.state === "CLAIMED") && !saveStoredRecovery(recovered)) {
      recoveryUnavailable = true;
      return;
    }
    if (legacyEncoded !== null) {
      access.storage.removeItem(LEGACY_CHECKOUT_RECOVERY_STORAGE_KEY);
      if (access.storage.getItem(LEGACY_CHECKOUT_RECOVERY_STORAGE_KEY) !== null) {
        recoveryUnavailable = true;
        return;
      }
    }
    pendingRecovery = recovered;
  } catch {
    recoveryUnavailable = true;
  }
}

export function readCheckoutRecovery(identityKey: string): CheckoutRecoveryView {
  hydrateStoredRecovery();
  if (recoveryUnavailable || !isIdentityKey(identityKey)) {
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  if (!pendingRecovery) return { status: "NONE" };
  if (pendingRecovery.ownerIdentityKey !== identityKey) return { status: "OTHER_OPERATOR" };
  return { status: "OWNED", recovery: pendingRecovery };
}

export function claimCheckoutRecovery(
  identityKey: string,
  command: CheckoutCommand,
): CheckoutRecoveryView {
  const current = readCheckoutRecovery(identityKey);
  if (current.status !== "NONE") return current;
  if (!isIdentityKey(identityKey) || !isCheckoutCommand(command)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  const recovery = immutableRecovery(identityKey, command, "CLAIMED");
  if (!saveStoredRecovery(recovery)) {
    recoveryUnavailable = true;
    return { status: "RECOVERY_STORAGE_UNAVAILABLE", pendingOutcomeMayExist };
  }
  pendingRecovery = recovery;
  pendingOutcomeMayExist = true;
  return { status: "OWNED", recovery };
}

function verifyRecoveryForDispatch(recovery: CheckoutRecovery, identityKey: string): boolean {
  hydrateStoredRecovery();
  if (
    recoveryUnavailable ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey ||
    !verifyStoredRecovery(recovery)
  ) {
    recoveryUnavailable = true;
    return false;
  }
  return true;
}

export function beginCheckoutDispatch(recovery: CheckoutRecovery, identityKey: string): boolean {
  if (dispatchingRecovery || !verifyRecoveryForDispatch(recovery, identityKey)) return false;
  dispatchingRecovery = recovery;
  return true;
}

export function finishCheckoutDispatch(recovery: CheckoutRecovery, identityKey: string): boolean {
  if (dispatchingRecovery !== recovery || recovery.ownerIdentityKey !== identityKey) return false;
  dispatchingRecovery = null;
  return true;
}

export function markCheckoutOutcomeUnknown(
  recovery: CheckoutRecovery,
  identityKey: string,
): boolean {
  if (!verifyRecoveryForDispatch(recovery, identityKey)) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : immutableRecovery(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStoredRecovery(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  pendingOutcomeMayExist = true;
  return true;
}

function expectedMemberDebitAmount(command: CheckoutCommand): number | null {
  if (command.memberId === null) return null;
  return (
    command.memberBalanceFen ??
    Math.max(0, command.baselineOrder.totalFen - command.baselineOrder.paidFen)
  );
}

function memberDebitMatchesCheckoutCommand(
  command: CheckoutCommand,
  memberDebit: unknown,
): memberDebit is MemberDebitState {
  const expectedAmountFen = expectedMemberDebitAmount(command);
  if (expectedAmountFen === null || !isMemberDebitState(memberDebit)) return false;
  if (
    memberDebit.amountFen !== expectedAmountFen ||
    Date.parse(memberDebit.updatedAt) < Date.parse(command.baselineOrder.startedAt)
  ) {
    return false;
  }
  if (memberDebit.status === "PENDING_MEMBER_DEBIT") {
    return command.method === "MEMBER_BALANCE" && command.memberBalanceFen === undefined;
  }
  if (memberDebit.status === "MEMBER_RECEIVABLE") {
    return command.method === "MEMBER_BALANCE" && command.memberBalanceFen === undefined;
  }
  return true;
}

export function applyCheckoutResultToRecovery(
  recovery: CheckoutRecovery,
  identityKey: string,
  memberDebit?: MemberDebitState,
): CheckoutRecoveryResult | null {
  if (!verifyRecoveryForDispatch(recovery, identityKey)) return null;
  const expectedAmountFen = expectedMemberDebitAmount(recovery.command);
  if (expectedAmountFen === null) {
    if (memberDebit !== undefined || !clearCheckoutRecovery(recovery, identityKey)) return null;
    return "CHECKOUT_CONFIRMED";
  }
  if (!memberDebitMatchesCheckoutCommand(recovery.command, memberDebit)) return null;
  if (memberDebit.status === "PENDING_MEMBER_DEBIT") {
    const pending = immutableRecovery(identityKey, recovery.command, "PENDING_MEMBER_DEBIT");
    if (!saveStoredRecovery(pending)) {
      recoveryUnavailable = true;
      return null;
    }
    pendingRecovery = pending;
    pendingOutcomeMayExist = true;
    return "PENDING_MEMBER_DEBIT";
  }
  if (!clearCheckoutRecovery(recovery, identityKey)) return null;
  return memberDebit.status === "MEMBER_RECEIVABLE" ? "MEMBER_RECEIVABLE" : "CHECKOUT_CONFIRMED";
}

export function clearCheckoutRecovery(recovery: CheckoutRecovery, identityKey: string): boolean {
  if (
    recoveryUnavailable ||
    pendingRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey
  ) {
    return false;
  }
  const access = sessionStorageAccess();
  if (access.browser) {
    if (!access.storage) {
      recoveryUnavailable = true;
      return false;
    }
    try {
      const stored = readStoredRecovery(access.storage);
      if (!stored || !recoveryEquals(stored, recovery)) {
        recoveryUnavailable = true;
        return false;
      }
      access.storage.removeItem(CHECKOUT_RECOVERY_STORAGE_KEY);
      access.storage.removeItem(LEGACY_CHECKOUT_RECOVERY_STORAGE_KEY);
      if (
        access.storage.getItem(CHECKOUT_RECOVERY_STORAGE_KEY) !== null ||
        access.storage.getItem(LEGACY_CHECKOUT_RECOVERY_STORAGE_KEY) !== null
      ) {
        recoveryUnavailable = true;
        return false;
      }
    } catch {
      recoveryUnavailable = true;
      return false;
    }
  }
  pendingRecovery = null;
  pendingOutcomeMayExist = false;
  return true;
}

export function checkoutOutcomeIsUnknown(error: unknown): boolean {
  return !(
    isRecord(error) &&
    typeof error.code === "string" &&
    definiteNoEffectCodes.has(error.code)
  );
}

function memberReceivableManualReviewIsValid(
  value: unknown,
): value is MemberReceivableManualReviewItem {
  if (!isRecord(value) || typeof value.createdAt !== "string") return false;
  if (
    !isManualReviewListReadModel({
      items: [value],
      pendingCount: 1,
      capacity: MANUAL_REVIEW_READ_CAPACITY,
      generatedAt: value.createdAt,
    })
  ) {
    return false;
  }
  const item = value as ManualReviewReadItem;
  return (
    item.category === "MEMBER_RECEIVABLE" &&
    item.itemKey === item.workItemId &&
    item.amountFen !== null &&
    Number.isSafeInteger(item.amountFen) &&
    item.amountFen > 0 &&
    item.offlineOrigin &&
    Date.parse(item.lastAttemptAt) >= Date.parse(item.createdAt)
  );
}

function memberReceivableRetryRequestIsValid(
  value: unknown,
): value is MemberReceivableRetryRequest {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["idempotencyKey"]) &&
    isBoundedString(value.idempotencyKey, 8, 128) &&
    /^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$/u.test(value.idempotencyKey)
  );
}

function memberReceivableRetryCommandIsValid(
  value: unknown,
): value is MemberReceivableRetryCommand {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["kind", "workItemId", "baselineReview", "request"]) &&
    value.kind === "MEMBER_RECEIVABLE_RETRY" &&
    memberReceivableManualReviewIsValid(value.baselineReview) &&
    value.workItemId === value.baselineReview.workItemId &&
    memberReceivableRetryRequestIsValid(value.request)
  );
}

function freezeDeep(value: unknown): void {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) return;
  for (const child of Object.values(value)) freezeDeep(child);
  Object.freeze(value);
}

function immutableMemberReceivableRetryRecovery(
  ownerIdentityKey: string,
  command: MemberReceivableRetryCommand,
  state: MemberReceivableRetryRecoveryState,
): MemberReceivableRetryRecovery {
  const recovery = structuredClone({ ownerIdentityKey, command, state });
  freezeDeep(recovery);
  return recovery;
}

function storedMemberReceivableRetryRecovery(value: unknown): MemberReceivableRetryRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !isRecord(value.recovery) ||
    !hasExactKeys(value.recovery, ["ownerIdentityKey", "command", "state"]) ||
    !isIdentityKey(value.recovery.ownerIdentityKey) ||
    !memberReceivableRetryCommandIsValid(value.recovery.command) ||
    (value.recovery.state !== "CLAIMED" && value.recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return immutableMemberReceivableRetryRecovery(
    value.recovery.ownerIdentityKey,
    value.recovery.command,
    value.recovery.state,
  );
}

function memberReceivableRetryRecoveryEquals(
  left: MemberReceivableRetryRecovery,
  right: MemberReceivableRetryRecovery,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function readStoredMemberReceivableRetryRecovery(
  storage: Storage,
): MemberReceivableRetryRecovery | null {
  const encoded = storage.getItem(MEMBER_RECEIVABLE_RETRY_RECOVERY_STORAGE_KEY);
  return encoded === null
    ? null
    : storedMemberReceivableRetryRecovery(JSON.parse(encoded) as unknown);
}

function saveStoredMemberReceivableRetryRecovery(recovery: MemberReceivableRetryRecovery): boolean {
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) return false;
  try {
    access.storage.setItem(
      MEMBER_RECEIVABLE_RETRY_RECOVERY_STORAGE_KEY,
      JSON.stringify({ version: 1, recovery }),
    );
    const stored = readStoredMemberReceivableRetryRecovery(access.storage);
    return stored !== null && memberReceivableRetryRecoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function hydrateMemberReceivableRetryRecovery(): void {
  if (memberReceivableRetryHydrated) return;
  memberReceivableRetryHydrated = true;
  const access = sessionStorageAccess();
  if (!access.browser) return;
  if (!access.storage) {
    memberReceivableRetryRecoveryUnavailable = true;
    return;
  }
  try {
    const encoded = access.storage.getItem(MEMBER_RECEIVABLE_RETRY_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    memberReceivableRetryPendingOutcomeMayExist = true;
    const stored = storedMemberReceivableRetryRecovery(JSON.parse(encoded) as unknown);
    if (!stored) {
      memberReceivableRetryRecoveryUnavailable = true;
      return;
    }
    const recovered =
      stored.state === "CLAIMED"
        ? immutableMemberReceivableRetryRecovery(
            stored.ownerIdentityKey,
            stored.command,
            "OUTCOME_UNKNOWN",
          )
        : stored;
    if (stored.state === "CLAIMED" && !saveStoredMemberReceivableRetryRecovery(recovered)) {
      memberReceivableRetryRecoveryUnavailable = true;
      return;
    }
    pendingMemberReceivableRetryRecovery = recovered;
  } catch {
    memberReceivableRetryRecoveryUnavailable = true;
  }
}

function memberReceivableRetryUnavailableView(): MemberReceivableRetryRecoveryView {
  return {
    status: "RECOVERY_STORAGE_UNAVAILABLE",
    canRetry: false,
    pendingOutcomeMayExist: memberReceivableRetryPendingOutcomeMayExist,
  };
}

export function memberReceivableRetryCommand(
  baselineReview: MemberReceivableManualReviewItem,
  request: MemberReceivableRetryRequest,
): MemberReceivableRetryCommand {
  return {
    kind: "MEMBER_RECEIVABLE_RETRY",
    workItemId: baselineReview.workItemId,
    baselineReview,
    request,
  };
}

export function readMemberReceivableRetryRecovery(
  identityKey: string,
): MemberReceivableRetryRecoveryView {
  hydrateMemberReceivableRetryRecovery();
  if (memberReceivableRetryRecoveryUnavailable || !isIdentityKey(identityKey)) {
    return memberReceivableRetryUnavailableView();
  }
  if (!pendingMemberReceivableRetryRecovery) return { status: "NONE", canRetry: false };
  if (pendingMemberReceivableRetryRecovery.ownerIdentityKey !== identityKey) {
    return { status: "OTHER_OPERATOR", canRetry: false };
  }
  return {
    status: "OWNED",
    recovery: pendingMemberReceivableRetryRecovery,
    canRetry: pendingMemberReceivableRetryRecovery.state === "OUTCOME_UNKNOWN",
  };
}

export function claimMemberReceivableRetryRecovery(
  identityKey: string,
  command: MemberReceivableRetryCommand,
): MemberReceivableRetryRecoveryView {
  const current = readMemberReceivableRetryRecovery(identityKey);
  if (current.status === "OWNED") {
    return JSON.stringify(current.recovery.command) === JSON.stringify(command)
      ? current
      : { status: "COMMAND_MISMATCH", canRetry: false };
  }
  if (current.status !== "NONE") return current;
  if (!isIdentityKey(identityKey) || !memberReceivableRetryCommandIsValid(command)) {
    memberReceivableRetryRecoveryUnavailable = true;
    return memberReceivableRetryUnavailableView();
  }
  const recovery = immutableMemberReceivableRetryRecovery(identityKey, command, "CLAIMED");
  if (!saveStoredMemberReceivableRetryRecovery(recovery)) {
    memberReceivableRetryRecoveryUnavailable = true;
    return memberReceivableRetryUnavailableView();
  }
  pendingMemberReceivableRetryRecovery = recovery;
  memberReceivableRetryPendingOutcomeMayExist = true;
  return { status: "OWNED", recovery, canRetry: false };
}

function verifyMemberReceivableRetryRecovery(
  recovery: MemberReceivableRetryRecovery,
  identityKey: string,
): boolean {
  hydrateMemberReceivableRetryRecovery();
  if (
    memberReceivableRetryRecoveryUnavailable ||
    pendingMemberReceivableRetryRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey
  ) {
    return false;
  }
  const access = sessionStorageAccess();
  if (!access.browser) return true;
  if (!access.storage) {
    memberReceivableRetryRecoveryUnavailable = true;
    return false;
  }
  try {
    const stored = readStoredMemberReceivableRetryRecovery(access.storage);
    if (!stored || !memberReceivableRetryRecoveryEquals(stored, recovery)) {
      memberReceivableRetryRecoveryUnavailable = true;
      return false;
    }
    return true;
  } catch {
    memberReceivableRetryRecoveryUnavailable = true;
    return false;
  }
}

export function beginMemberReceivableRetryDispatch(
  recovery: MemberReceivableRetryRecovery,
  identityKey: string,
): boolean {
  if (
    dispatchingMemberReceivableRetryRecovery ||
    !verifyMemberReceivableRetryRecovery(recovery, identityKey)
  ) {
    return false;
  }
  dispatchingMemberReceivableRetryRecovery = recovery;
  return true;
}

export function finishMemberReceivableRetryDispatch(
  recovery: MemberReceivableRetryRecovery,
  identityKey: string,
): boolean {
  if (
    dispatchingMemberReceivableRetryRecovery !== recovery ||
    recovery.ownerIdentityKey !== identityKey
  ) {
    return false;
  }
  dispatchingMemberReceivableRetryRecovery = null;
  return true;
}

export function markMemberReceivableRetryOutcomeUnknown(
  recovery: MemberReceivableRetryRecovery,
  identityKey: string,
): boolean {
  if (!verifyMemberReceivableRetryRecovery(recovery, identityKey)) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : immutableMemberReceivableRetryRecovery(identityKey, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStoredMemberReceivableRetryRecovery(unknown)) {
    memberReceivableRetryRecoveryUnavailable = true;
    return false;
  }
  pendingMemberReceivableRetryRecovery = unknown;
  memberReceivableRetryPendingOutcomeMayExist = true;
  return true;
}

function clearMemberReceivableRetryRecovery(
  recovery: MemberReceivableRetryRecovery,
  identityKey: string,
): boolean {
  if (!verifyMemberReceivableRetryRecovery(recovery, identityKey)) return false;
  const access = sessionStorageAccess();
  if (access.browser) {
    if (!access.storage) {
      memberReceivableRetryRecoveryUnavailable = true;
      return false;
    }
    try {
      access.storage.removeItem(MEMBER_RECEIVABLE_RETRY_RECOVERY_STORAGE_KEY);
      if (access.storage.getItem(MEMBER_RECEIVABLE_RETRY_RECOVERY_STORAGE_KEY) !== null) {
        memberReceivableRetryRecoveryUnavailable = true;
        return false;
      }
    } catch {
      memberReceivableRetryRecoveryUnavailable = true;
      return false;
    }
  }
  if (dispatchingMemberReceivableRetryRecovery === recovery) {
    dispatchingMemberReceivableRetryRecovery = null;
  }
  pendingMemberReceivableRetryRecovery = null;
  memberReceivableRetryPendingOutcomeMayExist = false;
  return true;
}

export function memberReceivableRetryResultMatchesCommand(
  result: unknown,
  command: MemberReceivableRetryCommand,
): result is MemberReceivableRetryResult {
  if (!memberReceivableRetryCommandIsValid(command) || !isMemberReceivableRetryResult(result)) {
    return false;
  }
  return (
    result.workItemId === command.workItemId &&
    result.amountFen === command.baselineReview.amountFen &&
    Date.parse(result.retriedAt) >= Date.parse(command.baselineReview.lastAttemptAt)
  );
}

export function completeMemberReceivableRetryRecovery(
  recovery: MemberReceivableRetryRecovery,
  identityKey: string,
  result: unknown,
): result is MemberReceivableRetryResult {
  return (
    verifyMemberReceivableRetryRecovery(recovery, identityKey) &&
    memberReceivableRetryResultMatchesCommand(result, recovery.command) &&
    clearMemberReceivableRetryRecovery(recovery, identityKey)
  );
}

export function memberReceivableRetryOutcomeIsUnknown(error: unknown): boolean {
  if (!isRecord(error)) return true;
  const statusCode = error.statusCode;
  return !(
    typeof statusCode === "number" &&
    Number.isInteger(statusCode) &&
    statusCode >= 400 &&
    statusCode < 500 &&
    statusCode !== 408
  );
}

export function clearMemberReceivableRetryAfterDefiniteRejection(
  recovery: MemberReceivableRetryRecovery,
  identityKey: string,
  error: unknown,
): boolean {
  return (
    !memberReceivableRetryOutcomeIsUnknown(error) &&
    clearMemberReceivableRetryRecovery(recovery, identityKey)
  );
}

export function resetMemberReceivableRetryRecoveryForTests(): void {
  pendingMemberReceivableRetryRecovery = null;
  dispatchingMemberReceivableRetryRecovery = null;
  memberReceivableRetryHydrated = false;
  memberReceivableRetryRecoveryUnavailable = false;
  memberReceivableRetryPendingOutcomeMayExist = false;
  try {
    sessionStorageAccess().storage?.removeItem(MEMBER_RECEIVABLE_RETRY_RECOVERY_STORAGE_KEY);
  } catch {
    // The next test starts from a fresh in-memory fail-closed state.
  }
}

export function resetMemberReceivableRetryRecoveryMemoryForTests(): void {
  pendingMemberReceivableRetryRecovery = null;
  dispatchingMemberReceivableRetryRecovery = null;
  memberReceivableRetryHydrated = false;
  memberReceivableRetryRecoveryUnavailable = false;
  memberReceivableRetryPendingOutcomeMayExist = false;
}

export function resetCheckoutRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
  const access = sessionStorageAccess();
  try {
    access.storage?.removeItem(CHECKOUT_RECOVERY_STORAGE_KEY);
    access.storage?.removeItem(LEGACY_CHECKOUT_RECOVERY_STORAGE_KEY);
  } catch {
    // Test cleanup also resets the in-memory fail-closed state.
  }
}

export function resetCheckoutRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
}
